import api from "./api";

function authHeaders() {
  const token = localStorage.getItem("tk446655440000");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const jsonHeaders = () => ({
  ...authHeaders(),
  "Content-Type": "application/json",
});

/**
 * Build JSON body for activate/deactivate/update.
 * API expects camelCase: teacherId, teacherIds, provinceId, …
 */
export function normalizeLicenseActionBody(body = {}) {
  const payload = {};

  const teacherId = body.teacherId ?? body.TeacherId;
  if (teacherId != null && teacherId !== "") {
    payload.teacherId = Number(teacherId);
  }

  const teacherIds = body.teacherIds ?? body.TeacherIds;
  if (Array.isArray(teacherIds) && teacherIds.length) {
    payload.teacherIds = teacherIds.map(Number).filter((n) => Number.isFinite(n));
  }

  const geoEntries = [
    ["provinceId", body.provinceId ?? body.ProvinceId],
    ["departmentId", body.departmentId ?? body.DepartmentId],
    ["subPrefectureId", body.subPrefectureId ?? body.SubPrefectureId],
    ["establishmentId", body.establishmentId ?? body.EstablishmentId],
  ];
  for (const [key, value] of geoEntries) {
    if (value != null && value !== "") payload[key] = Number(value);
  }

  if (!Object.keys(payload).length) {
    throw new Error("License action requires teacherId, teacherIds, or a geographic filter.");
  }

  return payload;
}

/** Body for POST /TeacherLicense/update (includes target license flag). */
export function normalizeLicenseUpdateBody(body = {}) {
  const payload = normalizeLicenseActionBody(body);
  const isLicensed = body.isLicensed ?? body.IsLicensed;
  if (isLicensed != null) payload.isLicensed = Boolean(isLicensed);
  return payload;
}

export function isLicenseApiFailure(res) {
  if (res == null) return false;
  const success = res.success ?? res.Success;
  return success === false;
}

export function getLicenseApiMessage(res, fallback = "") {
  return res?.message ?? res?.Message ?? fallback;
}

/** Pretty-print request/response for debug dialogs (temporary). */
export function formatLicenseApiDebug({
  endpoint = "",
  requestBody = null,
  status = null,
  responseBody = null,
  context = null,
} = {}) {
  const parts = [];
  if (endpoint) parts.push(`Endpoint: POST ${endpoint}`);
  if (status != null) parts.push(`HTTP status: ${status}`);
  if (context != null) {
    parts.push("UI context:");
    parts.push(JSON.stringify(context, null, 2));
  }
  parts.push("Request body:");
  parts.push(JSON.stringify(requestBody ?? {}, null, 2));
  parts.push("Response body:");
  parts.push(
    responseBody == null
      ? "null"
      : typeof responseBody === "string"
        ? responseBody
        : JSON.stringify(responseBody, null, 2)
  );
  return parts.join("\n");
}

/**
 * @returns {Promise<import('../layouts/teacher-license/types').TeacherLicenseSummary>}
 */
export async function fetchTeacherLicenseSummary() {
  const { data } = await api.get("/TeacherLicense/summary", { headers: authHeaders() });
  return data;
}

export function sanitizeTeacherLicenseRow(row = {}) {
  const pick = (camel, pascal = camel[0].toUpperCase() + camel.slice(1)) =>
    row[camel] ?? row[pascal];
  return Object.fromEntries(
    [
      ["id", pick("id", "Id")],
      ["regNumber", pick("regNumber")],
      ["name", pick("name")],
      ["preName", pick("preName")],
      ["phone", pick("phone")],
      ["whatsapp", pick("whatsapp")],
      ["email", pick("email")],
      ["regDate", pick("regDate")],
      ["isTeacher", pick("isTeacher")],
      ["isLicensed", pick("isLicensed")],
      ["hasProfilePic", pick("hasProfilePic")],
      ["provinceId", pick("provinceId")],
      ["provinceName", pick("provinceName")],
      ["departmentId", pick("departmentId")],
      ["departmentName", pick("departmentName")],
      ["subPrefectureId", pick("subPrefectureId")],
      ["subPrefectureName", pick("subPrefectureName")],
      ["establishmentId", pick("establishmentId")],
      ["establishmentName", pick("establishmentName")],
    ].filter(([, value]) => value !== undefined)
  );
}

export function parseTeacherLicensePage(payload = {}) {
  const rawRows = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.Data)
      ? payload.Data
      : [];
  const rawStats = payload?.stats ?? payload?.Stats ?? {};
  const number = (...values) => {
    const found = values.find((value) => value != null && value !== "");
    return Number(found ?? 0);
  };
  return {
    rows: rawRows.map(sanitizeTeacherLicenseRow),
    stats: {
      currentPage: number(rawStats.currentPage, rawStats.CurrentPage, rawStats.pageNumber, rawStats.PageNumber, 1),
      pageNumber: number(rawStats.pageNumber, rawStats.PageNumber, rawStats.currentPage, rawStats.CurrentPage, 1),
      pageSize: number(rawStats.pageSize, rawStats.PageSize),
      totalResults: number(rawStats.totalResults, rawStats.TotalResults, rawStats.totalCount, rawStats.TotalCount),
      totalCount: number(rawStats.totalCount, rawStats.TotalCount, rawStats.totalResults, rawStats.TotalResults),
      totalPages: number(rawStats.totalPages, rawStats.TotalPages),
      hasNextPage: Boolean(rawStats.hasNextPage ?? rawStats.HasNextPage),
      licensedPeople: number(rawStats.licensedPeople, rawStats.LicensedPeople),
      pendingActivationPeople: number(rawStats.pendingActivationPeople, rawStats.PendingActivationPeople),
      totalPeople: number(rawStats.totalPeople, rawStats.TotalPeople),
      maximumLicenses: number(rawStats.maximumLicenses, rawStats.MaximumLicenses),
      remainingLicenses: number(rawStats.remainingLicenses, rawStats.RemainingLicenses),
      isGlobalScope: Boolean(rawStats.isGlobalScope ?? rawStats.IsGlobalScope),
    },
  };
}

/**
 * @param {import('../layouts/teacher-license/types').TeacherLicenseListParams} params
 * @returns {Promise<import('../layouts/teacher-license/types').TeacherLicenseListResponse>}
 */
export async function fetchTeacherLicenseList(params, { signal } = {}) {
  const response = await api.get("/TeacherLicense/list", {
    params,
    headers: authHeaders(),
    signal,
  });
  if (process.env.NODE_ENV === "development") {
    const duration = response.headers?.["x-query-duration-ms"];
    const rows = response.headers?.["x-rows-returned"];
    if (duration != null || rows != null) {
      console.debug("[TeacherLicense] query diagnostics", { durationMs: duration, rowsReturned: rows });
    }
  }
  return parseTeacherLicensePage(response.data);
}

/**
 * @param {import('../layouts/teacher-license/types').TeacherLicenseUpdateRequest} body
 * @returns {Promise<import('../layouts/teacher-license/types').TeacherLicenseUpdateResponse>}
 */
export async function updateTeacherLicense(body) {
  const { data } = await api.post("/TeacherLicense/update", normalizeLicenseUpdateBody(body), {
    headers: jsonHeaders(),
  });
  return data;
}

/**
 * @param {import('../layouts/teacher-license/types').TeacherLicenseUpdateRequest} body
 * @returns {Promise<import('../layouts/teacher-license/types').TeacherLicenseUpdateResponse>}
 */
export async function activateTeachers(body) {
  const { data } = await api.post("/TeacherLicense/activate", normalizeLicenseActionBody(body), {
    headers: jsonHeaders(),
  });
  return data;
}

/**
 * @param {import('../layouts/teacher-license/types').TeacherLicenseUpdateRequest} body
 * @returns {Promise<import('../layouts/teacher-license/types').TeacherLicenseUpdateResponse>}
 */
export async function deactivateTeachers(body) {
  const { data } = await api.post("/TeacherLicense/deactivate", normalizeLicenseActionBody(body), {
    headers: jsonHeaders(),
  });
  return data;
}
